<template>
    <section
        v-if="blok"
        class="airport-subscribe"
        :class="{ 'has-bg-color': blok.bgColor?.color }"
        :style="`
            ${blok.bgColor?.color ? `background-color: ${blok.bgColor?.color};` : ''}
            ${blok.paddingY ? `padding-top: ${blok.paddingY}rem;` : ''}
            ${blok.paddingY ? `padding-bottom: ${blok.paddingY}rem;` : ''}
            ${blok.paddingX ? `padding-right: ${blok.paddingX}rem;` : ''}
            ${blok.paddingX ? `padding-left: ${blok.paddingX}rem;` : ''}
            ${blok.textColor?.color ? `color: ${blok.textColor?.color};` : ``}
        `"
    >
        <div
            id="email-subscription-section"
            class="container mx-auto flex flex-row scroll-mt-[100px]"
            :class="`${blok.contentAlign ? `content-${blok.contentAlign}` : ''}`"
        >
            <div class="title-wrapper">
                <h3 id="email-subscription-title">{{ blok.Title }}</h3>
                <p>{{ blok.Text }}</p>
            </div>
            <transition name="fade" mode="out-in">
                <div v-if="!emailSubscriptionStore.isSubscribed" class="input-wrapper">
                    <form class="md:flex md:w-full" novalidate @submit.prevent="submitForm">
                        <input
                            v-model="email"
                            type="email"
                            name="email"
                            :placeholder="blok.InputTextPlaceHolder"
                            :minlength="minLength"
                            :maxlength="maxLength"
                            class="w-full md:basis-2/3"
                            :class="{ 'has-error': v$.email.$error }"
                            :aria-label="blok.Title ? undefined : 'Email address'"
                            :aria-labelledby="blok.Title ? 'email-subscription-title' : undefined"
                            @input="onEmailInput"
                        />
                        <AdvancedBtn
                            v-if="apr"
                            :with-arrow="true"
                            component-type="button"
                            button-type="submit"
                            component-variant="primary"
                            :disabled="shouldDisableButton"
                        >
                            {{ blok.SubmitBtnText }}
                        </AdvancedBtn>

                        <button
                            v-else
                            type="submit"
                            class="w-full md:basis-1/3 transition-all duration-200 disabled:opacity-85 disabled:cursor-not-allowed"
                            :disabled="shouldDisableButton"
                        >
                            {{ blok.SubmitBtnText }}
                        </button>
                    </form>
                    <transition name="quick-fade">
                        <!-- Main error (required/format/disposable/server) -->
                        <div v-if="v$.email.$error" class="validation-message">
                            <span>{{ emailError }}</span>
                        </div>

                        <!-- Typo suggestion -->
                        <div v-else-if="suggestedEmail" class="validation-message">
                            <span class="underline cursor-pointer" @click="applyTypoSuggestion">
                                {{ t('forms.emailTypoSuggestion', { email: suggestedEmail }) }}
                            </span>
                        </div>

                        <!-- Role-based nudge -->
                        <div v-else-if="roleNudgeMessage" class="validation-message">
                            <span>{{ roleNudgeMessage }}</span>
                        </div>
                    </transition>
                </div>
                <div v-else class="message-success-wrapper">
                    <div class="text-center">
                        <div class="success__title">{{ blok.SubscribedTitle }}</div>
                        <div class="success__subtitle">
                            {{ blok.SubscribedSubTitle }}
                        </div>
                        <div class="success">
                            {{ blok.SubscribedTitle }}
                            <NuxtIcon name="ri:thumb-up-line" />
                        </div>
                    </div>
                </div>
            </transition>
        </div>
    </section>
</template>

<script setup lang="ts">
import { useEmailSubscriptionStore } from '@nuxt-monorepo/base/app/store/emailSubscription';
import type { IEmailSubscriptionConfig } from '@nuxt-monorepo/ui/types';
import AdvancedBtn from '@nuxt-monorepo/ui/components/AdvancedBtn.vue';
import { useEmailFieldValidation } from '@nuxt-monorepo/ui/composables/useEmailFieldValidation';

const props = defineProps<{
    blok: IEmailSubscriptionConfig;
    apr?: boolean;
    minLength?: number;
    maxLength?: number;
}>();

const { t } = useI18n();
const emailSubscriptionStore = useEmailSubscriptionStore();
const backendError: any = inject('backendError') || ref('');

const emit = defineEmits<{
    (e: 'submit', formData: { email: string }): void;
}>();

const email = ref<string>('');
const externalResults = ref<{ email?: string } | Record<string, never>>({});

const {
    v$,
    emailError,
    suggestedEmail,
    roleNudgeMessage,
    shouldDisableButton,
    onInput,
    applyTypoSuggestion,
    setServerError,
    validateBeforeSubmit,
} = useEmailFieldValidation({
    email,
    t,
    externalResults,
    minLength: props.minLength,
    maxLength: props.maxLength,
});

const onEmailInput = () => {
    onInput();
    // Clear backend error once user starts typing again
    backendError.value = '';
};

const submitForm = async () => {
    // Validate and check if can submit
    if (!validateBeforeSubmit()) {
        return;
    }

    // All clear - submit the form
    emit('submit', { email: email.value });
};

watch(
    () => backendError.value,
    (value) => {
        setServerError(value || null);
    }
);
</script>

<style scoped lang="postcss">
.airport-subscribe {
    @apply text-base text-[white] p-12;
    background: #023052;

    .container {
        @apply flex flex-wrap items-center justify-between;

        &.content-start {
            justify-content: start;
        }
        &.content-end {
            justify-content: end;
        }
        &.content-center {
            justify-content: center;

            .title-wrapper {
                @apply basis-auto;
            }
        }
        &.content-space-between {
            @apply justify-between;
        }

        .success__title {
            @apply text-2xl font-normal leading-none;
        }

        .success__subtitle {
            @apply text-sm font-normal leading-6;

            button {
                @apply text-[#44c8fe] underline border-[none];
            }
        }

        h3 {
            @apply font-medium text-[1.53125rem];
        }

        .validation-message {
            @apply text-sm text-[#ff3738] mt-1;
        }

        .title-wrapper,
        .input-wrapper,
        .message-success-wrapper {
            @apply w-full md:basis-1/2;
        }

        .message-success-wrapper {
            @apply flex items-center;

            > div {
                @apply w-full;
            }
        }

        form {
            input {
                @apply w-full bg-[white] px-4 py-[0.675rem] rounded-[5px] border-[none] text-black;

                &.has-error {
                    @apply border-[0.1rem] border-solid border-[red];
                }
            }

            button {
                @apply bg-[#f98f43] text-[white] px-12 py-[0.675rem] rounded-[5px] border-[none] ml-2;
            }
        }
    }
}

@media (max-width: 768px) {
    .airport-subscribe {
        .container {
            h3 {
                @apply font-medium text-[1.2rem];
            }

            form {
                input {
                    @apply w-full bg-[white] px-4 py-[0.675rem] rounded-[5px] border-[none];
                }

                button {
                    @apply ml-0 mt-2 md:mt-0;
                }
            }
        }
    }
}

.fade-enter-active,
.fade-leave-active {
    transition: opacity 0.5s ease-in-out;
}

.fade-enter-from,
.fade-leave-to {
    opacity: 0;
}
</style>
